import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { catchError, map, Observable, tap } from 'rxjs';
import { signal } from '@angular/core';

export type FocusAIGetUploadedFile =
  | {
      fileName: string;
      url: string;
      id: string;
    }
  | { fileName: string; error: string };
export type UploadingFile = FocusAIGetUploadedFile & {
  file: File;
  icon: string;
};

export type FocusAIGetUploadingFilesResponse = {
  data: FocusAIGetUploadedFile[];
};

export type FocusAIUploadedFile = {
  id: string;
  file: string;
  message_id: string;
  client_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  token_size?: string;
  size?: string;
  result?: string;
  reason?: string;
};

@Injectable()
export class FocusAiService {
  private isGeneratingSignal = signal<boolean>(false);

  readonly isGenerating = this.isGeneratingSignal.asReadonly();

  constructor(private http: HttpClient) {}

  turnOnGenerating(): void {
    this.isGeneratingSignal.set(true);
  }

  turnOffGenerating(): void {
    this.isGeneratingSignal.set(false);
  }

  reset(): void {
    this.isGeneratingSignal.set(false);
  }

  generate(
    clientId: string,
    messageId: string,
    prompt: string,
    productGroupId: string,
    prevState?: {
      text: string;
      actions: { type: string; label: string; value: string }[];
    },
  ) {
    this.turnOnGenerating();

    const focusAiUrl = `${environment.portalURLBase}/client/${clientId}/focus-ai/message/${messageId}/generate`;

    return this.http
      .post(
        focusAiUrl,
        {
          prompt,
          productGroupId,
          prevState: prevState,
        },
        {
          withCredentials: true,
        },
      )
      .pipe(
        tap((response: any) => this.turnOffGenerating()),
        catchError((error) => {
          this.turnOffGenerating();
          console.error('Error fetching file download URL:', error);
          throw error;
        }),
      );
  }

  sendFeedBack(
    clientId: string,
    messageId: string,
    feedback: 'positive' | 'negative',
  ) {
    const url = `${environment.portalURLBase}/client/${clientId}/focus-ai/message/${messageId}/feedback`;
    return this.http.post(url, { feedback }, { withCredentials: true });
  }

  buildCookieName(email: string, clientId: string): string {
    return `focus-ai-${clientId}-${email}`;
  }

  acceptTermsAndConditions(email: string, clientId: string): void {
    this.setCookie(this.buildCookieName(email, clientId), 'true');
  }

  hasUserAcceptedTermsAndConditions(email: string, clientId: string): boolean {
    const val = this.getCookie(this.buildCookieName(email, clientId));
    return val === 'true';
  }

  setCookie(cname: string, cvalue: string): void {
    document.cookie = `${cname}=${cvalue}; path=/;`;
  }

  getCookie(cname: string): string {
    let name = cname + '=';
    let ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  uploadFile(
    clientId: string,
    messageId: string,
    files: FileList,
  ): Observable<UploadingFile[]> {
    const filesArray = Array.from(files);

    const focusAiUrl = `${environment.portalURLBase}/client/${clientId}/focus-ai/message/${messageId}/files`;
    return this.http
      .post<FocusAIGetUploadingFilesResponse>(focusAiUrl, {
        files: Object.values(files).map((file) => file.name),
      })
      .pipe(
        map((response) =>
          response.data.map((result, index) => {
            return {
              file: filesArray[index],
              ...result,
              icon: this.getIconForFileType(result.fileName),
            };
          }),
        ),
      );
  }
  //WIP Get the presigned URL for the file download
  getFileDownloadUrl(clientId: string, messageId: string, fileName: string) {
    const focusAiUrl = `${environment.portalURLBase}/client/${clientId}/focus-ai/message/${messageId}/${fileName}`;
    return this.http
      .get<{ fileName: string; url: string }>(focusAiUrl, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching file download URL:', error);
          throw error;
        }),
      );
  }
  deleteFile(clientId: string, messageId: string, fileId: string) {
    const focusAiUrl = `${environment.portalURLBase}/client/${clientId}/focus-ai/message/${messageId}/file/${fileId}`;
    return this.http.delete(focusAiUrl, { withCredentials: true }).pipe(
      catchError((error) => {
        console.error('Error deleting file:', error);
        throw error;
      }),
    );
  }

  getFiles(clientId: string, messageId: string) {
    const focusAiUrl = `${environment.portalURLBase}/client/${clientId}/focus-ai/message/${messageId}`;
    return this.http.get<{ data: FocusAIUploadedFile[] }>(focusAiUrl);
  }

  getIconForFileType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'txt':
        return 'fa-file-lines';
      case 'eml':
        return 'fa-envelope';
      case 'html':
      case 'htm':
        return 'fa-file-code';
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint';
      default:
        return 'fa-file'; // Default icon for unknown file types
    }
  }

  getFileSize(size: string): string {
    const sizeInBytes = parseInt(size, 10);
    if (isNaN(sizeInBytes)) {
      return 'Unknown size';
    }
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} bytes`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    } else if (sizeInBytes < 1024 * 1024 * 102) {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
}
